import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} [productImage] - Product image element
 * @property {HTMLElement} [mobileStars] - Mobile-only star rating container (cloned from the Judge.me badge)
 * @property {HTMLSelectElement} [stickySizeSelect] - Mobile-only Size selector
 * @property {HTMLSelectElement} [stickyColorSelect] - Mobile-only Color selector
 * @property {HTMLElement} [stickyQuantityNumber] - Mobile-only quantity stepper's number display
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 *
 * Desktop: shows whenever the main buy buttons are not visible in the
 * viewport, regardless of scroll direction.
 *
 * Mobile: always visible from page load, across the whole page, and only
 * hides when the footer comes into view -- it deliberately ignores the buy
 * buttons' own visibility on mobile (see #buyButtonsIntersectionObserver).
 *
 * #isMobile is kept live via a matchMedia change listener (not just read
 * once at connectedCallback) -- otherwise a shopper who resizes or zooms
 * across the 749px breakpoint after page load would get stuck with
 * whichever behavior applied at load time (e.g. the bar staying
 * permanently shown, ignoring scroll, if the page happened to load under
 * 749px and was later widened).
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #isMobile = false;

  /** @type {MediaQueryList | null} */
  #mobileMediaQuery = null;

  connectedCallback() {
    super.connectedCallback();

    this.#mobileMediaQuery = window.matchMedia('(max-width: 749px)');
    this.#isMobile = this.#mobileMediaQuery.matches;

    this.#setupIntersectionObserver();

    // HB: on mobile the bar stays visible across the whole page (only the
    // footer observer hides it -- see #setupIntersectionObserver), so seed
    // the visible state immediately on load instead of waiting for the
    // shopper to scroll past the in-page buy buttons first. Desktop already
    // shows the real buy buttons within the first viewport and relies on
    // the intersection observer instead, so forcing it there would just
    // flash a redundant CTA before the observer corrects it.
    if (this.#isMobile) {
      this.#showStickyBar();
      this.#populateMobileStars();
    }

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    this.#mobileMediaQuery.addEventListener('change', this.#handleBreakpointChange, { signal });

    this.#getInitialQuantity();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility. Shows the sticky bar any time the
    // real buy buttons are out of view -- above OR below the viewport -- so
    // it stays correct when scrolling back up past them, not just when
    // scrolling down past them for the first time.
    //
    // HB: on mobile this observer is a no-op -- the bar's visibility there
    // is driven purely by proximity to the footer (#mainBottomObserver
    // below), so it stays visible across the whole page regardless of
    // whether the real in-page buy buttons happen to be in view.
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      if (this.#isMobile) return;

      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting) {
        if (!this.#isStuck) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
      } else if (this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again.
          if (this.#isMobile) {
            // Mobile always re-shows once the footer is out of view -- it
            // doesn't care where the real buy buttons are.
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
            return;
          }

          // Desktop: "not visible" covers both above the viewport (scrolled
          // past going down) and below the viewport (scrolled back up past
          // it).
          const rect = buyButtonsBlock.getBoundingClientRect();
          const buyButtonsVisible = rect.bottom > 0 && rect.top < window.innerHeight;
          if (!buyButtonsVisible) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles the mobile sticky bar's Size <select> changing. It puppets the
   * real variant picker's Size <select> (dispatching a genuine change
   * event) rather than re-implementing the variant-switch pipeline --
   * that keeps price, media, URL, and availability updates flowing through
   * the same single code path (assets/variant-picker.js), and this sticky
   * select gets naturally refreshed afterward via #handleProductSelect's
   * morph, same as everything else in the bar.
   * @param {Event} event
   */
  handleSizeChange = (event) => {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    if (!(select instanceof HTMLSelectElement)) return;

    const productId = this.dataset.productId;
    const mainPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    const realSelect = /** @type {HTMLSelectElement | null} */ (
      mainPicker?.querySelector('.variant-option--dropdowns select')
    );
    if (!realSelect || realSelect.value === select.value) return;

    realSelect.value = select.value;
    realSelect.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Handles the mobile sticky bar's Color <select> changing. Unlike Size,
   * the real Color control on the main variant picker is a set of swatch
   * radio inputs, not a <select> -- so this checks the matching radio by
   * value and dispatches a genuine change event on it instead of setting
   * a select value. Everything downstream (price, media, URL,
   * availability, and this sticky select's own re-sync) flows through the
   * same pipeline as handleSizeChange.
   * @param {Event} event
   */
  handleColorChange = (event) => {
    const select = /** @type {HTMLSelectElement} */ (event.target);
    if (!(select instanceof HTMLSelectElement)) return;

    const productId = this.dataset.productId;
    const mainPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!mainPicker) return;

    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
      mainPicker.querySelectorAll('.variant-option--swatches input[type="radio"]')
    );
    const targetRadio = Array.from(radios).find((radio) => radio.value === select.value);
    if (!targetRadio || targetRadio.checked) return;

    targetRadio.checked = true;
    targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Handles the mobile sticky bar's quantity minus button. Puppets the
   * real quantity-selector-component's own minus button (a plain click,
   * same pattern as handleAddToCartClick) so all of its existing logic --
   * min-quantity clamping, disabled state, ThemeEvents.quantitySelectorUpdate
   * dispatch -- keeps working unmodified. #handleQuantityUpdate (already
   * listening for that event) updates this sticky display in turn.
   */
  handleQuantityMinusClick = () => {
    const productForm = this.#getProductForm();
    const minusButton = productForm?.querySelector('quantity-selector-component .quantity-minus');
    if (minusButton instanceof HTMLElement) minusButton.click();
  };

  /**
   * Handles the mobile sticky bar's quantity plus button. See
   * handleQuantityMinusClick.
   */
  handleQuantityPlusClick = () => {
    const productForm = this.#getProductForm();
    const plusButton = productForm?.querySelector('quantity-selector-component .quantity-plus');
    if (plusButton instanceof HTMLElement) plusButton.click();
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // HB: refresh the unit/compare-at price this bar multiplies by
        // quantity (see #updatePrice) from the freshly server-rendered
        // hb-quantity-total-price element elsewhere in the morphed HTML --
        // that's already correctly computed for the newly selected variant,
        // so reuse it rather than guessing at the shape of `variant`.
        const freshQtyPrice = html.querySelector('hb-quantity-total-price');
        if (freshQtyPrice instanceof HTMLElement) {
          if (freshQtyPrice.dataset.unitPrice) this.dataset.unitPrice = freshQtyPrice.dataset.unitPrice;
          if (freshQtyPrice.dataset.compareAtPrice) this.dataset.compareAtPrice = freshQtyPrice.dataset.compareAtPrice;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();

        // The morph above replaces .sticky-add-to-cart__stars with a fresh,
        // empty server-rendered node -- re-clone the Judge.me badge into it.
        if (this.#isMobile) {
          this.#populateMobileStars();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Handles the viewport crossing the 749px breakpoint after load (window
   * resize or browser zoom). Without this, #isMobile would stay frozen at
   * whatever it was on page load, and the bar could get stuck showing (or
   * hidden) using the wrong breakpoint's rules -- e.g. a page that loaded
   * under 749px and was later widened would keep ignoring the real buy
   * button's visibility, since #buyButtonsIntersectionObserver no-ops
   * whenever #isMobile is (stale-)true.
   * @param {MediaQueryListEvent} event
   */
  #handleBreakpointChange = (event) => {
    this.#isMobile = event.matches;

    if (this.#isMobile) {
      this.#showStickyBar();
      this.#populateMobileStars();
      return;
    }

    // Just crossed into desktop -- re-sync immediately against the real buy
    // buttons' current on-screen position rather than waiting for the next
    // scroll to trigger #buyButtonsIntersectionObserver.
    const productForm = this.#getProductForm();
    const buyButtonsBlock = productForm?.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const rect = buyButtonsBlock.getBoundingClientRect();
    const buyButtonsVisible = rect.bottom > 0 && rect.top < window.innerHeight;
    if (buyButtonsVisible) {
      this.#hideStickyBar();
    } else if (!this.#isChatActive()) {
      this.#showStickyBar();
    }
  };

  /**
   * Clones the page's Judge.me star-rating badge into the mobile sticky
   * bar. The badge is an app block that can't be re-rendered ad-hoc from
   * this section, so it's cloned from the one already rendered elsewhere
   * on the PDP instead. Judge.me's own site-wide stylesheet drives the
   * clone's visibility/appearance the same way it does the original, so
   * this is safe to call repeatedly (e.g. after every morph).
   */
  #populateMobileStars() {
    const starsContainer = this.refs.mobileStars;
    if (!starsContainer) return;

    const original = document.querySelector('.jdgm-prev-badge');
    if (!original) return;

    starsContainer.replaceChildren(original.cloneNode(true));
  }

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber, stickyQuantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();
    if (stickyQuantityNumber) stickyQuantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }

    this.#updatePrice();
  }

  /**
   * HB: updates the sticky bar's own price display (both the mobile
   * stacked copy and the desktop inline copy -- see
   * .sticky-add-to-cart__price--mobile/--desktop in product-information.liquid)
   * to unit price x current quantity, with the compare-at price scaled the
   * same way. Mirrors the running total shown next to the real quantity
   * stepper (assets/hb-quantity-total-price.js), which this bar can't
   * reuse directly since that component requires a <product-form-component>
   * ancestor to find its quantity input, and the sticky bar lives outside
   * of one. data-unit-price/data-compare-at-price are seeded from the
   * selected variant in product-information.liquid and kept in sync on
   * variant change in #handleProductSelect.
   */
  #updatePrice() {
    const unitPrice = Number(this.dataset.unitPrice || 0);
    if (!unitPrice) return;
    const compareAtPrice = Number(this.dataset.compareAtPrice || 0);
    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';
    const quantity = this.#currentQuantity;

    // HB: 3-Pair Pack -- every 3rd pair of an eligible sock gets 40% off,
    // mirroring the "3-Pair Pack" automatic discount and the running total
    // next to the main quantity stepper (assets/hb-quantity-total-price.js).
    const packHandles = ['peruvian-alpaca-everyday-socks', 'fine-gauge-merino-wool-sock', 'peruvian-alpaca-lounge-socks'];
    const handleMatch = window.location.pathname.match(/\/products\/([^/?#]+)/);
    const packEligible = !!handleMatch && packHandles.includes(handleMatch[1]);
    const packs = packEligible ? Math.floor(quantity / 3) : 0;
    const total = unitPrice * quantity - packs * Math.round(unitPrice * 0.4);

    const priceEls = this.querySelectorAll('.sticky-add-to-cart__price .price');
    priceEls.forEach((el) => this.#setMoneyText(el, formatMoney(total, format, currency)));

    if (compareAtPrice > unitPrice) {
      const compareEls = this.querySelectorAll('.sticky-add-to-cart__price .compare-at-price');
      compareEls.forEach((el) => this.#setMoneyText(el, formatMoney(compareAtPrice * quantity, format, currency)));
    }
  }

  /**
   * Replaces just the trailing money text within a price snippet's
   * rendered output (see snippets/price.liquid), preserving the leading
   * visually-hidden "Sale price"/"Regular price" label span that precedes
   * it in the same element.
   * @param {Element} el
   * @param {string} text
   */
  #setMoneyText(el, text) {
    for (const node of el.childNodes) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent && node.textContent.trim()) {
        node.textContent = ` ${text} `;
        return;
      }
    }
    el.append(document.createTextNode(text));
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
